/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subscription} from 'rxjs';
import {ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferSignalStateWrite} from '../util/template-state';
import template from './top-articles-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_STAT_TYPES} from '../options/chart-options';
import {StatsApiService} from './stats-api.service';
import {AngularModalService} from '../modal/angular-modal.service';
import {AdnShotterService} from '../adn-shotter.service';
import {TopArticlesScreenshottingModalComponent} from './top-articles-screenshotting-modal.component';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {LineItemApiService} from '../../line-item/line-item-api.service';

@Component({
  standalone: false,
  selector: 'adn-top-articles-table',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopArticlesTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() children: any;
  @Input() parentType: string;
  @Input() dateSelectorId: string;

  sel: any = {};
  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  isLocalhost = false;
  parentParam: string;
  defaultTopN = 50;
  topN = 50;
  readonly loaded = signal(false);
  readonly displayLoaded = computed(() => this.loaded());
  private destroyed = false;
  readonly error = signal(false);
  readonly articles = signal<any[] | null>(null);
  readonly otherRow = signal<any>(null);
  readonly totals = signal<any>(null);
  readonly totalCount = signal<number | undefined>(undefined);
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);
  readonly dateOption = signal<any>(undefined);
  countType = ADN_CHART_COUNT_TYPES.singular;
  statType = ADN_CHART_STAT_TYPES.impression;
  useAlt: any;

  readonly screenshots = signal<any[] | null>(null);
  readonly screenshotArticles = signal<string[]>([]);
  readonly screenshotting = signal(false);
  readonly deleting = signal(false);
  readonly hasQueued = signal(false);
  readonly showScreenshots = signal(false);
  readonly isSaving = signal(false);

  readonly targetingUrls = signal<{[url: string]: string}>({});
  readonly targetingUrlsOpen = signal<string | false>(false);
  readonly openTargetingArticle = signal<string | null>(null);

  private chartDateListener: () => void;
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;
  private statsSubscription?: Subscription;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly ngZone: NgZone,
    private readonly angularModalService: AngularModalService,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly adnShotter: AdnShotterService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly destroyRef: DestroyRef,
    private readonly lineItemApi: LineItemApiService
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType || changes.dateSelectorId) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.isLocalhost = this.location.hostname === 'localhost';
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.initParams();

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;

    this.defaultTopN = 50;
    this.topN = this.defaultTopN || 50;

    this.fetchShots();
    this.checkTargeting();

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      this.applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      this.applyChartDates(lastDates);
    }
  }

  onTopNChange(newN: number): void {
    this.topN = newN;
    this.articles.set(null);
    this.error.set(false);
    this.callForStats();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    this.statsSubscription?.unsubscribe();
  }

  changeCallback(dateOptionId?: any, startDate?: any, endDate?: any): void {
    // Chart-options also broadcasts chartDates, but this callback updates the
    // bound dates first, so the listener would treat the range as unchanged
    // and skip the stats request. Apply the selected range here.
    this.applyChartDates({dateOptionId, startDate, endDate});
  }

  saveCallback(): void {
  }

  refresh(): void {
    this.fetchShots();
  }

  toggleScreenshots(): void {
    this.showScreenshots.update((visible) => !visible);
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting.set(true);
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      this.ngZone.run(() => {
        const screenshots = [...(this.screenshots() || [])];
        _.pullAt(screenshots, index);
        this.screenshots.set(screenshots);
      });
    }).finally(() => {
      this.ngZone.run(() => {
        this.deleting.set(false);
        if (!this.screenshots()?.length) {
          this.hasQueued.set(false);
        }
      });
    });
  }

  clearAllTargeting(): void {
    this.object.targeting.articleTarget.negated = false;
    this.object.targeting.articleTarget.urls = [];
    this.saveTargeting();
  }

  resetTargeting(): void {
    this.checkTargeting();
  }

  targetArticle(url: string): void {
    if (this.object.targeting.articleTarget.urls.length === 0) {
      this.object.targeting.articleTarget.negated = false;
    }
    this.object.targeting.articleTarget.urls.push(url);
    this.saveTargeting();
  }

  excludeArticle(url: string): void {
    if (this.object.targeting.articleTarget.urls.length === 0) {
      this.object.targeting.articleTarget.negated = true;
    }
    this.object.targeting.articleTarget.urls.push(url);
    this.saveTargeting();
  }

  removeFromTargeting(url: string): void {
    this.object.targeting.articleTarget.urls = _.filter(this.object.targeting.articleTarget.urls, (targetUrl) => {
      return targetUrl !== url;
    });
    this.saveTargeting();
  }

  toggleTargetingMenu(article: string): void {
    this.openTargetingArticle.set(this.openTargetingArticle() === article ? null : article);
  }

  async screenshottingOptions(articleUrls?: any): Promise<void> {
    const articles = this.filterScreenshots(
      _.isArray(articleUrls) ? articleUrls : _.isString(articleUrls) ? [articleUrls] : this.screenshotArticles()
    );
    const modalRef = await this.angularModalService.showAfterTick(TopArticlesScreenshottingModalComponent, {
      class: 'modal-lg',
      initialState: {
        object: this.object,
        children: this.children,
        articles,
        networkTag: this.LocalNetworkProfile.getNetworkTag()
      }
    }, this.destroyRef);
    if (!modalRef) {
      return;
    }
    const modalContent = modalRef.content as TopArticlesScreenshottingModalComponent;
    modalContent.onTriggered.subscribe(() => {
      this.ngZone.run(() => {
        this.hasQueued.set(true);
        this.fetchShots();
        this.showScreenshots.set(true);
      });
    });
  }

  triggerDefaultScreenshots(): void {
    const allPromises = [];
    _.forEach(this.screenshotArticles(), (article) => {
      allPromises.push(this.adnShotter.takeShot(null, this.object.id, article, 'DESKTOP'));
    });
    this.hasQueued.set(true);
    Promise.all(allPromises).then(() => {
      this.fetchShots();
      this.ngZone.run(() => {
        this.showScreenshots.set(true);
      });
    });
  }

  sortUpdate(field: any): void {
    if (field.key === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field.key;
    this.sorting.asc = true;
  }

  sortedArticles(): any[] {
    const articles = this.articles();
    if (!articles) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(articles, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  fieldValue(row: any, field: any): any {
    return row[this.useAlt ? (field.altId || field.id) : field.key];
  }

  private applyChartDates(data: {dateOptionId?: string; startDate?: any; endDate?: any}): void {
    const sameDate = (current: any, next: any) => {
      const currentMissing = current === undefined || current === null;
      const nextMissing = next === undefined || next === null;
      return currentMissing || nextMissing ? currentMissing && nextMissing : moment(current).isSame(next);
    };
    const datesUnchanged = () => this.startDate() !== 'UNDEFINED'
      && sameDate(this.startDate(), data.startDate)
      && sameDate(this.endDate(), data.endDate);
    const dateOptionUnchanged = () => this.dateOption()?.id === data.dateOptionId;
    if (datesUnchanged() && dateOptionUnchanged()) {
      return;
    }
    // Defer date bindings — lastDates / chartDates can land during parent CD
    // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
    deferSignalStateWrite(() => {
      if (this.destroyed) {
        return;
      }
      if (datesUnchanged() && dateOptionUnchanged()) {
        return;
      }

      this.dateOption.set(data.dateOptionId && this.chartOptionObject
        ? this.ChartOptionsUtil.getDateOptions(this.chartOptionObject.caps)[data.dateOptionId]
        : undefined);
      if (datesUnchanged()) {
        return;
      }

      this.initParams();

      this.startDate.set(data.startDate);
      this.endDate.set(data.endDate);

      this.articles.set(null);
      this.error.set(false);

      if (this.startDate() && this.endDate()) {
        this.params.startDate = this.startDate();
        this.params.endDate = this.endDate();
      }

      this.params.aggregateTo = 'article';
      this.params.groupBy = null;

      this.callForStats();
    });
  }

  private initParams(): void {
    this.params = {
      id: this.object.id,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };
  }

  private fetchShots(): void {
    this.screenshotting.set(true);
    this.adnShotter.fetchScreenshots(this.object.id).then((data: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        if (this.destroyed) {
          return;
        }
        const screenshots = data || [];
        _.forEach(screenshots, (s) => {
          s.articleUrlHttps = (s.articleUrl || '').indexOf('http') === 0 ? s.articleUrl : 'https://' + s.articleUrl;
        });
        this.screenshots.set(screenshots);
        this.hasQueued.set(!!_.find(screenshots, (s) => {
          return s.registrationStatus !== 'FINALISED';
        }));
      });
    }).finally(() => {
      deferSignalStateWrite(() => {
        if (!this.destroyed) {
          this.screenshotting.set(false);
        }
      });
    });
  }

  private checkTargeting(): void {
    const targetingUrls: {[url: string]: string} = {};
    const safeUrls = _.get(this.object, ['targeting', 'articleTarget', 'urls'], []);
    _.forEach(safeUrls, (url) => {
      targetingUrls[url] = this.object.targeting.articleTarget.negated ? 'EXCLUDED' : 'INCLUDED';
    });
    this.targetingUrls.set(targetingUrls);
    this.targetingUrlsOpen.set(false);
    if (safeUrls.length > 0) {
      this.targetingUrlsOpen.set(this.object.targeting.articleTarget.negated ? 'EXCLUDED' : 'INCLUDED');
    }
  }

  private saveTargeting(): void {
    this.isSaving.set(true);
    firstValueFrom(this.lineItemApi.savePartial(this.object, ['targeting'])).then((savedLineItem: any) => {
      this.ngZone.run(() => {
        this.checkTargeting();
        this.isSaving.set(false);
        this.eventBus.emit('lineItemTargetingUpdate', savedLineItem);
      });
    });
  }

  private filterScreenshots(articles: any[]): string[] {
    const selectedSites = [];
    return _.filter(_.map(articles, (art) => {
      if (!art || String(art).indexOf('all-others-stats-group-id') !== -1) {
        return null;
      }
      return (art || '').indexOf('http') === 0 ? art : 'https://' + art;
    }), (art) => {
      if (!art) {
        return false;
      }
      const url = new URL(art);
      const hostName = url.hostname;
      const isSelected = selectedSites.indexOf(hostName) < 0 && selectedSites.length < 3;
      if (isSelected) {
        selectedSites.push(hostName);
      }
      return isSelected;
    });
  }

  private callForStats(): void {
    this.params.topN = this.topN;
    this.loaded.set(false);
    this.statsSubscription?.unsubscribe();
    this.statsSubscription = this.statsApi.getArticles(this.params).subscribe({next: (data: any) => {
      const articles = data.chunks;

      const allOthersIndex = _.findIndex(data.chunks, (row: any) => {
        return row.article === 'all-others-stats-group-id';
      });

      const totals = data.totals;
      const totalCount = data.totalCount;

      const otherRow: any = allOthersIndex > -1 ? _.pullAt(data.chunks, allOthersIndex)[0] : null;
      if (otherRow) {
        const remainingObjects = data.totalCount - this.params.topN;
        otherRow.updatedName = remainingObjects + ' remaining articles';
      }

      const screenshotArticles = this.filterScreenshots(_.map(articles, 'article'));

      articles.forEach((article) => {
        if (article.matchScore) {
          if (article.matchScore === 'SLIGHTLY_RELATED') {
            article.matchScore = 1;
          }
          if (article.matchScore === 'SOMEWHAT_RELATED') {
            article.matchScore = 2;
          }
          if (article.matchScore === 'MODERATELY_RELATED') {
            article.matchScore = 3;
          }
          if (article.matchScore === 'CLOSELY_RELATED') {
            article.matchScore = 4;
          }
          if (article.matchScore === 'VERY_CLOSELY_RELATED') {
            article.matchScore = 5;
          }
        }
      });

      deferSignalStateWrite(() => {
        if (this.destroyed) {
          return;
        }
        this.articles.set(articles);
        this.totals.set(totals);
        this.totalCount.set(totalCount);
        this.otherRow.set(otherRow);
        this.screenshotArticles.set(screenshotArticles);
        this.loaded.set(true);
      });
    }, error: (err: any) => {
      this.adnLog.warn(err);
      deferSignalStateWrite(() => {
        if (this.destroyed) {
          return;
        }
        this.error.set(true);
        this.loaded.set(true);
      });
    }});
  }
}
